import * as amplitude from '@amplitude/analytics-browser'
import { Identify } from '@amplitude/analytics-browser'

const { VITE_ANALYTICS_KEY, MODE } = import.meta.env

type UserTrackingOptions = Record<string, string | number | boolean | string[]>

class Analytics {
  private static instance: Analytics
  private user: string | null = null

  private constructor() {
    // Disable analytics for localhost, dev, and staging environments
    if (
      window.location.hostname === 'localhost' ||
      !VITE_ANALYTICS_KEY ||
      MODE === 'development' ||
      MODE === 'staging'
    ) {
      return
    }

    const searchParams = new URLSearchParams(window.location.search)
    const platformParam = searchParams.get('platform')
    const isMacOS = platformParam === 'macos'

    if (window?.Cookiebot?.consent?.statistics || isMacOS) {
      this.initAnalytics()
    } else {
      window.addEventListener('CookiebotOnAccept', this.initAnalytics, false)
    }
  }

  private initAnalytics(): void {
    window.removeEventListener('CookiebotOnAccept', this.initAnalytics, false)

    amplitude.init(VITE_ANALYTICS_KEY as string, undefined, {
      autocapture: {
        pageViews: true,
        networkTracking: true,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: true,
        attribution: true,
      },
      defaultTracking: {
        pageViews: true,
      },
      trackingOptions: {
        ipAddress: true,
        language: true,
        platform: true,
      },
    })

    window.addEventListener(
      'CookiebotOnDecline',
      () => {
        amplitude.setOptOut(true)
      },
      false,
    )

    window.addEventListener(
      'CookiebotOnAccept',
      () => {
        amplitude.setOptOut(false)
      },
      false,
    )
  }

  public static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics()
    }
    return Analytics.instance
  }

  public setUser(id: string, options?: UserTrackingOptions): void {
    if (!id || this.user) return
    this.user = id
    amplitude.setUserId(id)
    if (options) {
      const identifyObj = new Identify()
      Object.keys(options).forEach(key => {
        identifyObj.set(key, options[key])
      })
      amplitude.identify(identifyObj)
    }
  }

  public logEvent(
    eventName: string,
    eventProperties?: Record<string, string | number>,
  ): void {
    amplitude.track(eventName, eventProperties)
  }
}
export const analytics = Analytics.getInstance()
